import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountActionSuccess from './AccountActionSuccess';
import Text from '@/components/Text';
import RulaButton from '@pathccm/rula-ui/button';
import fetch from '@/utils/fetch';
import { Trigger } from '@/utils/types';
import { usePatientAccountDetails } from '@/utils/use-personal-details';

interface DeclareGuardianProps {
  trackAccountAction: (action: {
    name: string;
    cta_type?: string;
    metadata?: any;
    trigger?: Trigger;
  }) => void;
}

const DeclareGuardian = ({ trackAccountAction }: DeclareGuardianProps) => {
  const [view, setView] = useState<
    'declare-guardian' | 'declare-guardian-success'
  >('declare-guardian');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { patientUuid, patientName } = usePatientAccountDetails();
  const handleSubmit = async () => {
    trackAccountAction({
      name: 'medicalGuardianshipPage_CTAClicked',
      cta_type: 'confirm',
    });
    setSubmitting(true);
    setFormError(null);
    try {
      await fetch.json('/api/update_relationship_type', {
        method: 'POST',
        body: {
          // we should always have this, but it is from a query param
          patient_uuid: patientUuid!,
          relationship_type: 'MEDICAL',
        },
      });
      setView('declare-guardian-success');
    } catch (e) {
      trackAccountAction({
        name: 'failure',
        metadata: { account_action: 'medical_guardian', failure_reason: e },
      });
      console.error(e);
      setFormError('Failed to declare medical guardianship.');
    }
    setSubmitting(false);
  };

  if (view === 'declare-guardian-success') {
    return (
      <AccountActionSuccess
        copy="Your decision to declare medical guardianship has been recorded."
        refreshAccounts={() => {
          window.location.href = '/';
        }}
        isGuardianFlow
        trackAccountAction={trackAccountAction}
      />
    );
  }

  return (
    <div className="flex flex-col justify-center w-full max-w-[305px] items-center gap-6 text-center">
      <Text.H1>Medical guardianship</Text.H1>
      <Text.P>
        You’ve chosen to declare medical guardianship of {patientName} beyond
        their 18th birthday. If this is correct, please confirm your decision
        and follow the instructions on the following page.
      </Text.P>
      {formError && <Text.P className="text-red-50">{formError}</Text.P>}
      <RulaButton
        loading={submitting}
        onPress={handleSubmit}
        size="Large"
        className="w-full"
        style="Primary"
      >
        Confirm
      </RulaButton>
      <RulaButton
        style="Link"
        size="Medium"
        onPress={() => {
          trackAccountAction({
            name: 'medicalGuardianshipPage_CTAClicked',
            cta_type: 'back',
          });
          navigate(`/account-transfer/${patientUuid}`);
        }}
      >
        &larr; Back
      </RulaButton>
    </div>
  );
};
export default DeclareGuardian;
